import type { Waypoint, WaypointType } from './types';
import type { CostingModel } from '@/api/geo';

// Compact waypoint format for URL
interface CompactWaypoint {
  a: number; // lat
  o: number; // lon
  l: string; // label
  t?: WaypointType; // type
}

/**
 * Encode waypoints to base64 string for URL
 */
export function encodeRouteToUrl(waypoints: Waypoint[], costing: CostingModel): string {
  // Convert to compact format
  const compact: CompactWaypoint[] = waypoints.map((w) => ({
    a: Math.round(w.lat * 100000) / 100000, // 5 decimal places
    o: Math.round(w.lon * 100000) / 100000,
    l: w.label,
    t: w.type,
  }));

  try {
    const json = JSON.stringify(compact);
    const base64 = btoa(unescape(encodeURIComponent(json)));
    const params = new URLSearchParams({ r: base64, m: costing });
    return `/route-planner?${params.toString()}`;
  } catch {
    return '/route-planner';
  }
}

/**
 * Decode waypoints from URL search params
 */
export function decodeRouteFromUrl(searchParams: URLSearchParams): {
  waypoints: Waypoint[];
  costing: CostingModel;
} | null {
  const routeParam = searchParams.get('r');
  const costingParam = searchParams.get('m') as CostingModel | null;

  if (!routeParam) {
    return null;
  }

  try {
    const json = decodeURIComponent(escape(atob(routeParam)));
    const compact: CompactWaypoint[] = JSON.parse(json);

    const waypoints: Waypoint[] = compact.map((c, index) => ({
      id: `url-${index}-${Date.now()}`,
      lat: c.a,
      lon: c.o,
      label: c.l,
      type: c.t,
    }));

    return {
      waypoints,
      costing: costingParam || 'auto',
    };
  } catch {
    return null;
  }
}

/**
 * Generate a share URL for the current route
 */
export function generateShareUrl(waypoints: Waypoint[], costing: CostingModel): string {
  const path = encodeRouteToUrl(waypoints, costing);
  return `${window.location.origin}${path}`;
}

/**
 * Check if URL is too long for sharing (> 2000 chars is risky)
 */
export function isUrlTooLong(url: string): boolean {
  return url.length > 2000;
}

/**
 * Generate unique ID for waypoints
 */
export function generateWaypointId(): string {
  return `wp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
